import React from 'react';
import { Modal, Button, TextField, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import { Close } from '@mui/icons-material';
import { YardSpot } from '../../pages/YardSpotterApp/yardDataFetch';

interface LocalYardSpot extends YardSpot {
  direction?: 'inbound' | 'outbound' | null;
}

interface DockDoorInfo {
  door: string;
  dockUuid: string;
  zoneUuid: string;
  zoneName: string;
  hasMoveFromTask?: boolean;
}

interface MoveToDockModalProps {
  open: boolean;
  selectedSpot: LocalYardSpot | null;
  selectedShipmentPuid: string;
  selectedDockDoor: string;
  dockFilter: string;
  autoCompleteMoveToYardSpot: boolean;
  moveToDockError: string | null;
  movingToDock: boolean;
  validatingShipment: boolean;
  loadingDockDoors: boolean;
  activeDockDoors: DockDoorInfo[];
  isLoadedTrailer: (spot: LocalYardSpot) => boolean;
  onClose: () => void;
  onMove: () => void;
  onShipmentPuidChange: (value: string) => void;
  onDockDoorChange: (value: string) => void;
  onDockFilterChange: (value: string) => void;
  onAutoCompleteChange: (checked: boolean) => void;
}

const MoveToDockModal: React.FC<MoveToDockModalProps> = ({
  open,
  selectedSpot,
  selectedShipmentPuid,
  selectedDockDoor,
  dockFilter,
  autoCompleteMoveToYardSpot,
  moveToDockError,
  movingToDock,
  validatingShipment,
  loadingDockDoors,
  activeDockDoors,
  isLoadedTrailer,
  onClose,
  onMove,
  onShipmentPuidChange,
  onDockDoorChange,
  onDockFilterChange,
  onAutoCompleteChange,
}) => {
  if (!selectedSpot) return null;

  const handleDockFilterChange = (filterValue: string) => {
    onDockFilterChange(filterValue);
    
    // Auto-selection logic
    if (filterValue.trim()) {
      // Find dock doors that match the filter
      const matchingDocks = activeDockDoors.filter(dockInfo => 
        dockInfo.door.toLowerCase().includes(filterValue.toLowerCase())
      );
      
      // Check for exact match
      const exactMatch = matchingDocks.find(dockInfo => 
        dockInfo.door.toLowerCase() === filterValue.toLowerCase()
      );
      
      // Auto-select if there's exactly 1 result and it's an exact match
      if (matchingDocks.length === 1 && exactMatch) {
        onDockDoorChange(exactMatch.dockUuid);
      } else {
        // Clear selection if multiple results or no exact match
        onDockDoorChange('');
      }
    } else {
      // Clear selection when filter is empty
      onDockDoorChange('');
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="flex justify-center items-center w-full h-full">
        <div className="w-[450px] bg-white border p-5 max-h-[90vh] overflow-y-auto relative">
          <Close
            className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
            onClick={onClose}
          />
          <h1 className="text-xl font-bold mb-4">Move Trailer to Dock Door</h1>
          
          {/* Trailer details */}
          <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <span className="font-semibold">Spot:</span> {selectedSpot.displayNumber}
              </div>
              {selectedSpot.trailerNumber && (
                <div className="col-span-2">
                  <span className="font-semibold">Trailer:</span> {selectedSpot.trailerNumber}
                </div>
              )}
              {selectedSpot.carrier_name && (
                <div className="col-span-2">
                  <span className="font-semibold">SCAC:</span> {selectedSpot.carrier_name}
                </div>
              )}
            </div>
          </div>
          
          {/* Show error if any */}
          {moveToDockError && (
            <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {moveToDockError}
            </div>
          )}
          
          {/* Shipment Reference Number field */}
          <div className="mb-4">
            <h2 className="font-semibold mb-2">Shipment Reference Number *</h2>
            <TextField
              label="Enter Reference Number *"
              variant="outlined"
              fullWidth
              value={selectedShipmentPuid}
              onChange={(e) => onShipmentPuidChange(e.target.value)}
              helperText={isLoadedTrailer(selectedSpot) ? "Reference number is read-only for loaded trailers" : "Shipment reference number is required (e.g. REF12345)"}
              disabled={validatingShipment || movingToDock || isLoadedTrailer(selectedSpot)}
              error={!selectedShipmentPuid && moveToDockError?.includes('required')}
            />
          </div>
          
          {/* Auto-complete checkbox */}
          <div className="mb-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoCompleteMoveToYardSpot}
                  onChange={(e) => onAutoCompleteChange(e.target.checked)}
                  disabled={movingToDock || validatingShipment}
                />
              }
              label="Auto-complete task (task will be marked as completed immediately)"
            />
          </div>
          
          {/* Dock door selection */}
          <div className="mb-4">
            <h2 className="font-semibold mb-2">Select Dock Door</h2>
            
            {/* Loading state */}
            {loadingDockDoors ? (
              <div className="flex justify-center items-center py-2">
                <CircularProgress size={24} />
                <span className="ml-2 text-sm text-gray-600">Loading available dock doors...</span>
              </div>
            ) : (
              <>
                <TextField
                  label="Filter Dock Doors"
                  variant="outlined"
                  fullWidth
                  value={dockFilter}
                  onChange={(e) => handleDockFilterChange(e.target.value)}
                  helperText="Type to filter dock doors below (e.g. D10)"
                  placeholder="Type dock door name to filter..."
                />
                
                {/* Only show available dock doors */}
                {activeDockDoors.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm text-gray-600 mb-1">Available Docks:</div>
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        // Filter dock doors based on dock filter
                        const filteredDockDoors = activeDockDoors.filter(dockInfo => {
                          const matchesFilter = !dockFilter.trim() || dockInfo.door.toLowerCase().includes(dockFilter.toLowerCase());
                          return matchesFilter;
                        });
                        
                        if (filteredDockDoors.length === 0) {
                          return (
                            <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 w-full">
                              {dockFilter.trim() 
                                ? `No dock doors found matching "${dockFilter}". Try clearing the filter.`
                                : `No dock doors available.`
                              }
                            </div>
                          );
                        }
                        
                        return filteredDockDoors.map(dockInfo => (
                          <button
                            key={dockInfo.dockUuid}
                            className={`px-2 py-1 border rounded text-sm ${
                              selectedDockDoor === dockInfo.dockUuid 
                                ? 'bg-blue-100 border-blue-500' 
                                : dockInfo.hasMoveFromTask 
                                  ? 'bg-gray-300 hover:bg-gray-400' 
                                  : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                            onClick={() => {
                              onDockDoorChange(dockInfo.dockUuid);
                              onDockFilterChange(dockInfo.door);
                            }}
                            title={dockInfo.hasMoveFromTask ? 'Door has an active move-from task' : undefined}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium">{dockInfo.door}</span>
                              <span className="text-xs text-gray-500">{dockInfo.zoneName}</span>
                            </div>
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                )}
                
                {/* No available dock doors message */}
                {activeDockDoors.length === 0 && !loadingDockDoors && (
                  <div className="mt-3 text-amber-600 bg-amber-50 p-2 border border-amber-200 rounded text-sm">
                    No active dock doors are currently available. You can still manually enter a dock door number.
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="flex justify-between pt-3 border-t">
            <Button onClick={onClose}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={onMove}
              disabled={!selectedDockDoor.trim() || movingToDock || validatingShipment}
            >
              {validatingShipment ? (
                <>
                  <CircularProgress size={16} className="mr-2" />
                  Validating...
                </>
              ) : movingToDock ? (
                <>
                  <CircularProgress size={16} className="mr-2" />
                  Moving...
                </>
              ) : (
                'Move to Dock'
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MoveToDockModal;

